import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, ExternalLink, FolderOpen, Search, CheckCheck, Undo2, MailOpen, ChevronDown, ChevronUp, Settings2, FilePlus2, UserRound, Monitor } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ComunicacaoPje, fetchComunicacoesPorOab, onlyDigits } from '@/services/comunicaPjeService';
import { detectJudicialSystem, normalizePersonName } from '@/utils/judicialSystem';
import { IntimacaoClient, RegisterFromIntimacaoDialog } from '@/components/intimacoes/RegisterFromIntimacaoDialog';

const OAB_KEY = 'intimacoes_oab';
const READ_KEY = 'intimacoes_lidas';
const READ_BACKUP_KEY = 'intimacoes_lidas_antes';
const DEFAULT_OAB = { numero: '67173', uf: 'PE' };

const loadJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const saveJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // armazenamento indisponível: segue sem persistir
  }
};

const formatDateBR = (iso: string) => iso.split('-').reverse().join('/');

interface SystemCase {
  id: string;
  title: string;
}

const Intimacoes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [oab, setOab] = useState(() => loadJson(OAB_KEY, DEFAULT_OAB));
  const [oabDraft, setOabDraft] = useState(oab);
  const [showOabConfig, setShowOabConfig] = useState(false);
  const [days, setDays] = useState('30');
  const [items, setItems] = useState<ComunicacaoPje[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('processo') || '');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [readIds, setReadIds] = useState<Set<number>>(() => new Set(loadJson<number[]>(READ_KEY, [])));
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [casesByCnj, setCasesByCnj] = useState<Map<string, SystemCase>>(new Map());
  const [clients, setClients] = useState<IntimacaoClient[]>([]);
  const [caseCountByClient, setCaseCountByClient] = useState<Record<string, number>>({});
  const [registering, setRegistering] = useState<ComunicacaoPje | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fim = new Date();
      const inicio = new Date(fim.getTime() - Number(days) * 24 * 60 * 60 * 1000);
      setItems(await fetchComunicacoesPorOab(oab.numero, oab.uf, inicio, fim));
    } catch (e: any) {
      setError(e?.message || 'Erro ao buscar intimações');
    } finally {
      setLoading(false);
    }
  }, [oab, days]);

  useEffect(() => {
    load();
  }, [load]);

  // Processos e clientes do sistema, para ligar cada intimação ao que já existe
  useEffect(() => {
    (async () => {
      const [casesRes, clientsRes] = await Promise.all([
        supabase.from('cases').select('id, title, case_number, case_number_cnj, client_id'),
        supabase.from('clients').select('id, name, email').order('name'),
      ]);
      const map = new Map<string, SystemCase>();
      const counts: Record<string, number> = {};
      (casesRes.data || []).forEach((c: any) => {
        [c.case_number_cnj, c.case_number].forEach((n) => {
          const digits = onlyDigits(n);
          if (digits.length >= 15) map.set(digits, { id: c.id, title: c.title });
        });
        if (c.client_id) counts[c.client_id] = (counts[c.client_id] || 0) + 1;
      });
      setCasesByCnj(map);
      setCaseCountByClient(counts);
      setClients((clientsRes.data || []) as IntimacaoClient[]);
    })();
  }, []);

  const clientsByName = useMemo(() => new Map(clients.map((c) => [normalizePersonName(c.name), c])), [clients]);
  const clientFor = (item: ComunicacaoPje) =>
    item.destinatarios.map((d) => clientsByName.get(normalizePersonName(d.nome))).find(Boolean) || null;

  const updateRead = (next: Set<number>) => {
    setReadIds(next);
    saveJson(READ_KEY, [...next]);
  };

  const toggleRead = (id: number) => {
    if (undoIds) {
      setUndoIds(null);
      saveJson(READ_BACKUP_KEY, null);
    }
    const next = new Set(readIds);
    next.has(id) ? next.delete(id) : next.add(id);
    updateRead(next);
  };

  // Guarda o estado anterior para poder desfazer
  const [undoIds, setUndoIds] = useState<number[] | null>(() => loadJson<number[] | null>(READ_BACKUP_KEY, null));
  const markAllRead = () => {
    const previous = [...readIds];
    setUndoIds(previous);
    saveJson(READ_BACKUP_KEY, previous);
    updateRead(new Set([...readIds, ...items.map((i) => i.id)]));
    toast({ title: 'Todas marcadas como lidas', description: 'Use "Desfazer" se foi sem querer.' });
  };
  const undoMarkAll = () => {
    if (!undoIds) return;
    updateRead(new Set(undoIds));
    setUndoIds(null);
    saveJson(READ_BACKUP_KEY, null);
  };
  const markAllUnread = () => {
    const ids = new Set(items.map((i) => i.id));
    updateRead(new Set([...readIds].filter((id) => !ids.has(id))));
    setUndoIds(null);
    saveJson(READ_BACKUP_KEY, null);
  };

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const saveOab = () => {
    const numero = onlyDigits(oabDraft.numero);
    const uf = oabDraft.uf.trim().toUpperCase();
    if (!numero || uf.length !== 2) {
      toast({ title: 'OAB inválida', description: 'Informe o número e a UF (ex.: 67173 / PE).', variant: 'destructive' });
      return;
    }
    const value = { numero, uf };
    setOab(value);
    saveJson(OAB_KEY, value);
    setShowOabConfig(false);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const termDigits = onlyDigits(term);
    return items.filter((i) => {
      if (onlyUnread && readIds.has(i.id)) return false;
      if (!term) return true;
      if (termDigits.length >= 4 && i.numero_processo.includes(termDigits)) return true;
      return [i.texto, i.nomeOrgao, i.siglaTribunal, i.tipoDocumento, ...i.destinatarios.map((d) => d.nome)]
        .some((v) => v?.toLowerCase().includes(term));
    });
  }, [items, search, onlyUnread, readIds]);

  const unreadCount = items.filter((i) => !readIds.has(i.id)).length;
  const notRegistered = new Set(items.filter((i) => !casesByCnj.has(i.numero_processo)).map((i) => i.numero_processo)).size;

  const outlineBtn = 'h-9 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-700 shadow-none hover:border-[#c4ab83] hover:bg-[#faf7f2] hover:text-slate-900';

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-4 md:p-6">
      {/* Cabeçalho */}
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Diário de Justiça Eletrônico Nacional</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Intimações</h1>
          <p className="mt-1 text-sm text-slate-500">
            OAB {oab.numero}/{oab.uf}
            {items.length > 0 && ` · ${unreadCount} não lida${unreadCount === 1 ? '' : 's'}`}
            {notRegistered > 0 && ` · ${notRegistered} processo${notRegistered === 1 ? '' : 's'} ainda não cadastrado${notRegistered === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowOabConfig((v) => !v)} className={outlineBtn}>
            <Settings2 className="mr-1.5 h-4 w-4 text-[#9a7f52]" /> OAB
          </Button>
          {undoIds ? (
            <Button onClick={undoMarkAll} className={outlineBtn}>
              <Undo2 className="mr-1.5 h-4 w-4 text-[#9a7f52]" /> Desfazer
            </Button>
          ) : unreadCount === 0 && items.length > 0 ? (
            <Button onClick={markAllUnread} className={outlineBtn}>
              <MailOpen className="mr-1.5 h-4 w-4 text-[#9a7f52]" /> Marcar todas como não lidas
            </Button>
          ) : (
            <Button onClick={markAllRead} disabled={unreadCount === 0} className={outlineBtn}>
              <CheckCheck className="mr-1.5 h-4 w-4 text-[#9a7f52]" /> Marcar todas como lidas
            </Button>
          )}
          <Button onClick={load} disabled={loading} className="h-9 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800">
            <RefreshCw className={`mr-1.5 h-4 w-4 text-[#e8d5a8] ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>
      </header>

      {showOabConfig && (
        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200/80 bg-white p-4 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-500">Número da OAB</label>
            <Input value={oabDraft.numero} onChange={(e) => setOabDraft({ ...oabDraft, numero: e.target.value })} className="mt-1 rounded-lg border-slate-200 shadow-none" />
          </div>
          <div className="w-full sm:w-24">
            <label className="text-xs font-medium text-slate-500">UF</label>
            <Input maxLength={2} value={oabDraft.uf} onChange={(e) => setOabDraft({ ...oabDraft, uf: e.target.value })} className="mt-1 rounded-lg border-slate-200 shadow-none" />
          </div>
          <Button onClick={saveOab} className="rounded-xl bg-[#c4ab83] font-semibold text-slate-900 hover:bg-[#b39a72]">Salvar</Button>
        </div>
      )}

      {/* Busca e filtros */}
      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200/80 bg-white p-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-10 rounded-xl border-0 bg-slate-50 pl-9 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
            placeholder="Buscar por número do processo, parte, vara ou texto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-10 w-full rounded-xl border-slate-200 text-sm shadow-none sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 dias</SelectItem>
            <SelectItem value="15">Últimos 15 dias</SelectItem>
            <SelectItem value="30">Últimos 30 dias</SelectItem>
            <SelectItem value="60">Últimos 60 dias</SelectItem>
            <SelectItem value="90">Últimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setOnlyUnread((v) => !v)}
          className={`h-10 rounded-xl px-4 text-[13px] font-medium transition-colors ${onlyUnread ? 'bg-[#c4ab83]/20 text-[#7a6440]' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
        >
          Só não lidas
        </button>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-xl">Tentar novamente</Button>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="rounded-2xl border border-slate-200/80 bg-white px-6 py-12 text-center text-sm text-slate-400">Buscando intimações...</div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-400">
          Nenhuma intimação encontrada no período.
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item) => {
          const isRead = readIds.has(item.id);
          const isExpanded = expanded.has(item.id);
          const systemCase = casesByCnj.get(item.numero_processo);
          const system = detectJudicialSystem(item.link, item.siglaTribunal);
          const client = clientFor(item);
          const clientCases = client ? caseCountByClient[client.id] || 0 : 0;
          return (
            <article
              key={item.id}
              className={`relative overflow-hidden rounded-2xl border bg-white p-5 transition-colors ${isRead ? 'border-slate-200/80 opacity-75' : 'border-[#c4ab83]/40'}`}
            >
              {!isRead && <span className="absolute inset-y-0 left-0 w-1 bg-[#c4ab83]" />}

              <div className="flex flex-wrap items-center gap-2">
                {!isRead && <span className="rounded-full bg-[#c4ab83] px-2 py-0.5 text-[11px] font-semibold text-slate-900">Nova</span>}
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white" title="Sistema onde o processo tramita">
                  <Monitor className="h-3 w-3 text-[#e8d5a8]" /> {system.label}
                </span>
                <span className="text-sm font-semibold tabular-nums text-slate-800">{formatDateBR(item.data_disponibilizacao)}</span>
                <span className="text-xs text-slate-400">
                  {item.tipoComunicacao}
                  {item.tipoDocumento && ` · ${item.tipoDocumento.replace(/_/g, ' / ')}`}
                </span>
              </div>

              <div className="mt-3">
                <p className="text-base font-semibold tracking-tight text-slate-900">Processo {item.numeroprocessocommascara}</p>
                <p className="text-sm text-slate-500">
                  {item.nomeOrgao}
                  {item.nomeClasse && ` · ${item.nomeClasse}`}
                </p>
                {item.destinatarios.length > 0 && (
                  <p className="mt-0.5 text-sm text-slate-700">Partes: {item.destinatarios.map((d) => d.nome).join(', ')}</p>
                )}
              </div>

              <p className={`mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-600 ${isExpanded ? '' : 'line-clamp-3'}`}>{item.texto}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <button type="button" onClick={() => toggleExpanded(item.id)} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-[13px] text-slate-500 hover:bg-slate-50 hover:text-slate-800">
                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {isExpanded ? 'Recolher' : 'Ler tudo'}
                </button>

                {systemCase ? (
                  <Button onClick={() => navigate(`/processo/${systemCase.id}`)} className="h-9 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800">
                    <FolderOpen className="mr-1.5 h-4 w-4 text-[#e8d5a8]" /> Abrir processo
                  </Button>
                ) : (
                  <Button onClick={() => setRegistering(item)} className="h-9 rounded-xl bg-[#c4ab83] text-[13px] font-semibold text-slate-900 hover:bg-[#b39a72]">
                    <FilePlus2 className="mr-1.5 h-4 w-4" /> Cadastrar processo
                  </Button>
                )}

                {client && (
                  <button
                    type="button"
                    onClick={() => navigate(`/clients?open=${client.id}`)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-700 hover:bg-emerald-100"
                    title="Abrir o cliente e os processos dele"
                  >
                    <UserRound className="h-4 w-4" />
                    {client.name.split(' ')[0]} · {clientCases} {clientCases === 1 ? 'processo' : 'processos'}
                  </button>
                )}

                {item.link && (
                  <Button asChild className={outlineBtn}>
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-1.5 h-4 w-4 text-[#9a7f52]" /> Ver no {system.system}
                    </a>
                  </Button>
                )}

                <button type="button" onClick={() => toggleRead(item.id)} className="ml-auto rounded-lg px-2 py-1.5 text-[13px] text-slate-500 hover:bg-slate-50 hover:text-slate-800">
                  {isRead ? 'Marcar como não lida' : 'Marcar como lida'}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <RegisterFromIntimacaoDialog item={registering} clients={clients} onOpenChange={(open) => !open && setRegistering(null)} />
    </div>
  );
};

export default Intimacoes;
